<template>
  <div class="avatar" :class="[`avatar--${size}`, `avatar--${shape}`]">
    <img
      v-if="pictureUrl && !imgError"
      :src="pictureUrl"
      :alt="`${firstName} ${lastName}`"
      class="avatar__img"
      @error="imgError = true"
    />
    <span v-else-if="initials" class="avatar__initials">{{ initials }}</span>
    <UserIcon v-else class="avatar__icon" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { UserIcon } from '@heroicons/vue/24/solid'

const props = withDefaults(
  defineProps<{
    firstName: string
    lastName: string
    pictureUrl?: string
    size?: 'sm' | 'md' | 'lg'
    shape?: 'circle' | 'squircle'
  }>(),
  {
    size: 'md',
    shape: 'circle',
  },
)

const imgError = ref(false)

const initials = computed(() =>
  ((props.firstName?.[0] ?? '') + (props.lastName?.[0] ?? '')).toUpperCase(),
)
</script>

<style scoped lang="scss">
.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--app-sidebar-bg);
  color: #fff;
  font-weight: var(--font-weight-semibold);
}

// Sizes
.avatar--sm {
  width: 24px;
  height: 24px;
  font-size: var(--text-size-xs);
}

.avatar--md {
  width: 32px;
  height: 32px;
  font-size: var(--text-size-sm);
}

.avatar--lg {
  width: 96px;
  height: 96px;
  font-size: var(--text-size-3xl);
}

// Shapes
.avatar--circle {
  border-radius: 50%;
  border: 2px solid rgba(var(--app-brand-rgb, 90, 110, 255), 0.2);
}

.avatar--squircle {
  border-radius: 20%;
  border: none;
}

.avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar__initials {
  line-height: 1;
  user-select: none;
}

.avatar__icon {
  width: 65%;
  height: 65%;
  color: currentColor;
}
</style>
